/**
 * @Description 推荐机会点
 */
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from 'antd';
import { isArray, isNotEmpty, refactorPermissions } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { associateFranchiseeToTask, getFranchiseeRecommendOppList } from '@/common/api/expandStore/franchisee';
import { useGlobalAliases } from '@/common/hook/useGlobalAlias';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
// import cs from 'classnames';
// import styles from './index.module.less';
import V2Table from '@/common/components/Data/V2Table';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import V2Operate from '@/common/components/Others/V2Operate';
import ChanceDetailDrawer from '@/common/components/business/ExpandStore/ChanceDetailDrawer';
import ExpansionFranchiseeCircleTaskDrawer from '@/views/expandstore/pages/expansionFranchiseeCircleTask/pages/detail/Drawer';

const { Text } = Typography;

interface RecommendChancePointProps {
  titleText: string;
  /** 加盟商ID，推荐机会点按加盟商维度实时计算 */
  franchiseeId?: number | string;
};

/**
 * @Description 推荐机会点
 */

const RecommendChancePoint: FC<RecommendChancePointProps> = ({
  titleText,
  franchiseeId, // 加盟商ID
}) => {
  /** 获取全局别名 */
  const {
    standardChancePoint: standardChancePointAlias, // 机会点别名
    standardTask: standardTaskAlias,
  } = useGlobalAliases();
  const tableRef: any = useRef(null); // 列表实例，用于操作成功后刷新当前页
  const taskDrawerRef: any = useRef(null); // 拓店任务详情抽屉实例，通过 init(taskId) 打开
  const [chanceDrawerOpen, setChanceDrawerOpen] = useState<boolean>(false); // 机会点详情抽屉开关
  const [currentChanceId, setCurrentChanceId] = useState<number | null>(null); // 当前查看的机会点ID
  const [renderKey, setRenderKey] = useState<number>(0); // 表格重挂载标记
  // table的filters为浅比较依赖，需保持引用稳定，避免每次渲染都重新请求
  const filters = useMemo(() => ({ franchiseeId }), [franchiseeId]);

  // 别名为异步获取，重新挂载table使异步获取的别名在defaultColumns里生效
  useEffect(() => {
    setRenderKey((state) => ++state);
  }, [standardChancePointAlias, standardTaskAlias]);

  const methods = useMethods({
    /**
     * @description 加载推荐机会点列表数据
     * @return table数据，接口实时计算最多返回 3 条并按推荐度降序
     */
    loadData() {
      if (!isNotEmpty(franchiseeId)) {
        // 没有加盟商ID时不请求接口，直接返回空列表
        return Promise.resolve({
          dataSource: [],
          count: 0,
        });
      }
      return getFranchiseeRecommendOppList({ franchiseeId }).then((data) => {
        const { list } = data || {};
        return {
          dataSource: isArray(list) ? list : [],
          count: isArray(list) ? list.length : 0,
        };
      });
    },

    /**
     * @description 点击机会点名称，打开机会点详情抽屉
     * @param record 当前行数据
     */
    handleChancePointClick(record: any) {
      setCurrentChanceId(record?.chancePointId ?? null);
      setChanceDrawerOpen(true);
    },

    /**
     * @description 点击关联拓店任务名称，打开拓店任务详情抽屉
     * @param record 当前行数据
     */
    handleTaskClick(record: any) {
      if (!isNotEmpty(record?.taskId)) return; // 没有关联任务时不响应点击
      taskDrawerRef.current?.init(record.taskId);
    },

    /**
     * @description 关联操作，二次确认后将该加盟商关联到机会点所属的拓店任务
     * 与 refactorPermissions 约定的方法名保持一致（event: standardFranchisee:recommendOppAssociate）
     * @param record 当前行数据
     */
    handleRecommendOppAssociate(record: any) {
      V2Confirm({
        content: '确认关联？',
        onOk: () => {
          methods.confirmAssociat(record);
        },
      });
    },

    /**
     * @description 确认关联，调用关联接口，成功后刷新列表
     * @param record 当前行数据
     */
    confirmAssociat(record: any) {
      associateFranchiseeToTask({
        franchiseeId,
        chancePointId: record?.chancePointId,
      }).then(() => {
        V2Message.success('操作成功');
        // 关联成功后该机会点会从推荐列表中移除，因此刷新当前页
        tableRef.current?.onload(true);
      });
    },

  });

  const defaultColumns = [
    {
      key: 'chancePointName',
      title: `${standardChancePointAlias}名称`,
      width: 220,
      render: (value: any, record: any) => {
        return isNotEmpty(value) ? (
          <Text
            style={{ width: 220 }}
            ellipsis={{ tooltip: value }}
            className='c-006'
          >
            <span
              className='color-primary-operate'
              onClick={() => methods.handleChancePointClick(record)}
            >
              {value}
            </span>
          </Text>
        ) : (
          '-'
        );
      },
    },
    { key: 'statusName', title: `${standardChancePointAlias}状态`, width: 'auto', },
    {
      key: 'taskName',
      title: `关联${standardTaskAlias}`,
      width: 150,
      render: (value: any, record: any) => {
        return isNotEmpty(value) && isNotEmpty(record?.taskId) ? (
          <Text
            style={{ width: 150 }}
            ellipsis={{ tooltip: value }}
            className='c-006'
          >
            <span
              className='color-primary-operate'
              onClick={() => methods.handleTaskClick(record)}
            >
              {value}
            </span>
          </Text>
        ) : (
          '-'
        );
      },
    },
    {
      key: 'recommendScore',
      title: '推荐度（%）',
      width: 120,
      render: (value: any) => isNotEmpty(value) ? `${value}%` : '-',
    },
    { key: 'developerName', title: '开发人员', width: 120, },
    {
      key: 'permissions',
      title: '操作',
      fixed: 'right',
      width: 120,
      dragChecked: true,
      dragDisabled: true,
      render: (val: any[], record: any) => (
        <V2Operate
          operateList={refactorPermissions(isArray(val) ? val : [])}
          onClick={(btn: any) => methods[btn.func](record)}
        />
      ),
    },
  ];

  return (
    <>
      <V2Title type='H2' text={titleText} divider className='mt-16 mb-16'/>

      <V2Table
        ref={tableRef}
        key={renderKey}
        rowKey='chancePointId'
        defaultColumns={defaultColumns}
        onFetch={methods.loadData}
        pagination={false}
        hideColumnPlaceholder={true}
        filters={filters}
        voluntarilyEmpty
        scroll={{ x: 'max-content', y: 380 }}
      />

      {/* 机会点详情抽屉 */}
      <ChanceDetailDrawer
        id={currentChanceId}
        open={chanceDrawerOpen}
        setOpen={setChanceDrawerOpen}
      />

      {/* 拓店任务详情抽屉 */}
      <ExpansionFranchiseeCircleTaskDrawer
        ref={taskDrawerRef}
      />
    </>
  );
};

export default RecommendChancePoint;
